import { useMemo } from 'react';

import type { ILead } from 'src/services/api';
import type { IMessage } from 'src/services/api/messages/types';
import { useCreateFactExtraction } from 'src/services/api/leads/use-create-fact-extraction';
import { useLeadFacts } from 'src/services/api/leads/use-lead-facts';
import { usePersistRegexSuggestions } from 'src/services/api/leads/use-persist-regex-suggestions';
import {
    NO_EXTRACTION,
    computeExtractionRequest,
    computeRuleSuggestions,
    pendingRowSuggestions,
} from 'src/services/lead-facts/suggestions';
import type { IFactSuggestion } from 'src/services/lead-facts/types';

const NO_SUGGESTIONS: never[] = [];

/**
 * Lead-fact suggestions, recomputed as the conversation changes. Every suggestion —
 * LLM (income/household/voucher) and client-detected regex (phone/email/pet) — is
 * persisted as a pending row in the backend and surfaced here from those rows, so both
 * sources share one lifecycle. LLM values come from budget-enforced extraction runs;
 * regex values are detected client-side and persisted via usePersistRegexSuggestions.
 * `enabled=false` short-circuits the whole pipeline.
 */
export const useLeadFactSuggestions = (
    lead: ILead,
    messages: IMessage[],
    enabled = true
): { suggestions: IFactSuggestion[]; isLoading: boolean } => {
    const { data, isSuccess: areFactsLoaded, isLoading } = useLeadFacts(lead.uuid, enabled);
    const storedSuggestions = data ?? NO_SUGGESTIONS;

    /* Wait for the stored rows before requesting a run or persisting detections: they
       gate whether a run is still needed and which regex values are already stored. */
    const ready = enabled && areFactsLoaded;

    const request = useMemo(
        () => (ready ? computeExtractionRequest(messages, storedSuggestions, lead) : NO_EXTRACTION),
        [messages, storedSuggestions, lead, ready]
    );
    useCreateFactExtraction(lead.uuid, request);

    const detections = useMemo(
        () =>
            ready ?
                computeRuleSuggestions(messages, lead, storedSuggestions).map(({ key, value }) => ({
                    key,
                    value,
                })) :
                [],
        [messages, lead, storedSuggestions, ready]
    );
    usePersistRegexSuggestions(lead.uuid, detections, ready);

    const suggestions = useMemo(
        () => (enabled ? pendingRowSuggestions(storedSuggestions, lead) : NO_SUGGESTIONS),
        [storedSuggestions, lead, enabled]
    );

    return { suggestions, isLoading: enabled && isLoading };
};
